import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SaviQuiz from "../components/SaviQuiz";
import { ArrowRight, ArrowDown, Pause } from "lucide-react";

const sg = { fontFamily: "'Space Grotesk', sans-serif" };
const sm = { fontFamily: "'Space Mono', monospace" };

const COWORKING_IMG = "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/lkhx9niz_20260401_133049-EDIT.jpg";
const STEVE_IMG = "https://customer-assets.emergentagent.com/job_savi-community-1/artifacts/g8yzc0zl_IMG_20250708_133040-EDIT.jpg";

const img = {
  villa: "https://static.wixstatic.com/media/447ef7_c4f040f251774723aaf22ea2f5874e83~mv2.jpg/v1/fill/w_1200,h_800,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/447ef7_c4f040f251774723aaf22ea2f5874e83~mv2.jpg",
  room: "https://static.wixstatic.com/media/7c2ee1_5c9d290dc3a24769a660b1582425dae2~mv2.jpg/v1/fill/w_1200,h_800,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/7c2ee1_5c9d290dc3a24769a660b1582425dae2~mv2.jpg",
  mountain: "https://static.wixstatic.com/media/875a66_5cb88f7c3e35456e92c395f35a9c030b~mv2.jpg",
  living: "https://static.wixstatic.com/media/447ef7_a7112dc842e742219a0a65e143a4e71f~mv2.jpg/v1/fill/w_1200,h_800,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/447ef7_a7112dc842e742219a0a65e143a4e71f~mv2.jpg",
};

const Label = ({ children, className = "" }) => (
  <span className={`text-[10px] tracking-[0.35em] uppercase ${className}`} style={sg}>{children}</span>
);

const Num = ({ n, className = "text-black/15" }) => (
  <span className={`text-[11px] tracking-[0.5em] font-bold ${className}`} style={sm}>{String(n).padStart(3, '0')}</span>
);

/* Shared nav for the SI-style pages */
export const SiNav = ({ light = false }) => {
  const base = light ? "text-white" : "text-[#1A1A1A]";
  const muted = light ? "text-white/60 hover:text-white" : "text-[#1A1A1A]/50 hover:text-[#1A1A1A]";
  return (
    <nav className={`fixed top-0 left-0 right-0 z-[60] ${light ? '' : 'bg-[#F8F6F1]/90 backdrop-blur-lg border-b border-black/5'}`}>
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 h-14 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/rooms" className="hidden sm:block"><Label className={`${muted} transition-colors`}>Rooms</Label></Link>
          <Link to="/about" className="hidden sm:block"><Label className={`${muted} transition-colors`}>About</Label></Link>
        </div>
        <Link to="/" className="absolute left-1/2 -translate-x-1/2">
          <span className={`${base} text-sm tracking-[0.5em] font-light`} style={{fontFamily: 'Cinzel, serif'}}>SAVI</span>
        </Link>
        <div className="flex items-center gap-8">
          <Link to="/sober-coliving" className="hidden sm:block"><Label className={`${muted} transition-colors`}>Journal</Label></Link>
          <Link to="/contact"><Label className={`${base} transition-colors hover:text-[#C85A38]`}>Apply</Label></Link>
        </div>
      </div>
    </nav>
  );
};

const HomeV2Page = () => {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const h = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F6F1]" style={sg}>
      <Helmet>
        <title>SAVI COLIVING | VALENCIA</title>
        <meta name="description" content="Application-only coliving for founders and creatives in Valencia, Spain. No alcohol. No distractions. Just focus." />
        <link rel="canonical" href="https://www.savicoliving.com" />
      </Helmet>

      {/* HERO — full viewport, dark overlay, white text */}
      <section className="relative h-screen overflow-hidden">
        {/* Nav on hero is light (white text) */}
        <nav className="fixed top-0 left-0 right-0 z-[60]" style={{ opacity: scrollY < 700 ? 1 : 0, transition: 'opacity 0.3s' }}>
          <div className="max-w-[1600px] mx-auto px-6 sm:px-10 h-14 flex items-center justify-between">
            <div className="flex items-center gap-8">
              <Link to="/rooms" className="hidden sm:block"><Label className="text-white/60 hover:text-white transition-colors">Rooms</Label></Link>
              <Link to="/about" className="hidden sm:block"><Label className="text-white/60 hover:text-white transition-colors">About</Label></Link>
            </div>
            <Link to="/" className="absolute left-1/2 -translate-x-1/2">
              <span className="text-white text-sm tracking-[0.5em] font-light" style={{fontFamily: 'Cinzel, serif'}}>SAVI</span>
            </Link>
            <div className="flex items-center gap-8">
              <Link to="/sober-coliving" className="hidden sm:block"><Label className="text-white/60 hover:text-white transition-colors">Journal</Label></Link>
              <button onClick={() => document.getElementById('v2-quiz')?.scrollIntoView({ behavior: 'smooth' })}>
                <Label className="text-white hover:text-[#C85A38] transition-colors">Apply</Label>
              </button>
            </div>
          </div>
        </nav>

        {/* Solid nav appears after scroll past hero */}
        <div style={{ opacity: scrollY >= 700 ? 1 : 0, transition: 'opacity 0.3s', pointerEvents: scrollY >= 700 ? 'auto' : 'none' }}>
          <SiNav />
        </div>

        <video autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: `translateY(${scrollY * 0.2}px)` }}
          poster={img.mountain}
        >
          <source src="https://video.wixstatic.com/video/f539fd_dd46caed36484dc99c7b55ef39a38ba2/1080p/mp4/file.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="absolute inset-0 flex flex-col justify-end pb-16 sm:pb-24 px-6 sm:px-10"
          style={{ opacity: Math.max(0, 1 - scrollY / 500) }}>
          <div className="max-w-[1600px] mx-auto w-full">
            <div className="flex items-start justify-between">
              <div>
                <Num n={1} className="text-white/25" />
                <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl text-white mt-4 mb-5 leading-[1.05] tracking-[0.02em] font-light max-w-3xl">
                  SOBER COLIVING.<br />THE MOUNTAIN RESIDENCE.
                </h1>
                <p className="text-white/60 text-xs sm:text-sm max-w-md leading-relaxed mb-8">
                  A private villa in the mountains of Valencia for founders and creatives. No alcohol. No distractions. Application only.
                </p>
                <button onClick={() => document.getElementById('v2-quiz')?.scrollIntoView({ behavior: 'smooth' })} className="group">
                  <Label className="text-white/80 hover:text-white transition-colors border-b border-white/30 pb-1 group-hover:border-white">Apply to stay</Label>
                </button>
              </div>
              <Num n={2} className="text-white/15 hidden md:block" />
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4">
          <Pause className="w-3 h-3 text-white/30" /><div className="w-8 h-px bg-white/20" /><ArrowDown className="w-3 h-3 text-white/30 animate-bounce" />
        </div>
      </section>

      {/* 002 — THE VILLA — light bg */}
      <section className="min-h-screen grid md:grid-cols-2">
        <div className="relative overflow-hidden min-h-[50vh] md:min-h-screen">
          <img src={img.villa} alt="Savi villa" className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-16 sm:py-24 bg-[#F8F6F1]">
          <Num n={2} />
          <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-5 mb-8 leading-[1.1] tracking-[0.02em] font-light">
            A VILLA IN THE<br />MOUNTAINS.
          </h2>
          <p className="text-[#1A1A1A]/50 text-sm leading-relaxed max-w-sm mb-6">
            Private property in the Sierra Calderona, 25 minutes from Valencia. Pool, gym, roof terrace with views that make you forget you're working.
          </p>
          <p className="text-[#1A1A1A]/50 text-sm leading-relaxed max-w-sm mb-10">
            up to 16 residents at a time. Minimum one month. Everyone's here because they chose to be — and because we chose them.
          </p>
          <Link to="/rooms"><Label className="text-[#1A1A1A]/40 hover:text-[#1A1A1A] transition-colors border-b border-[#1A1A1A]/15 pb-1 hover:border-[#1A1A1A]/40">View rooms</Label></Link>
        </div>
      </section>

      {/* 003 — COWORKING — reversed */}
      <section className="min-h-screen grid md:grid-cols-2">
        <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-16 sm:py-24 bg-[#F4F1EC] order-2 md:order-1">
          <Num n={3} />
          <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-5 mb-8 leading-[1.1] tracking-[0.02em] font-light">
            BUILT FOR<br />DEEP WORK.
          </h2>
          <p className="text-[#1A1A1A]/50 text-sm leading-relaxed max-w-sm mb-6">
            Dual screens. Starlink 500Mbps. Standing desks. Natural light from floor-to-ceiling windows overlooking the sierra.
          </p>
          <p className="text-[#1A1A1A]/50 text-sm leading-relaxed max-w-sm mb-10">
            Weekly skill shares. Group accountability. A room full of people who are actually shipping.
          </p>
          <div className="flex gap-10">
            {[{ num: "500", label: "MBPS WIFI" }, { num: "2", label: "SCREENS" }, { num: "24/7", label: "ACCESS" }].map((s, i) => (
              <div key={i}>
                <p className="text-[#C85A38] text-2xl font-light">{s.num}</p>
                <p className="text-[#1A1A1A]/20 text-[9px] tracking-[0.3em] mt-1" style={sm}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative overflow-hidden min-h-[50vh] md:min-h-screen order-1 md:order-2">
          <img src={COWORKING_IMG} alt="Coworking at Savi" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </section>

      {/* FULL BLEED DIVIDER */}
      <section className="relative h-[50vh] sm:h-[60vh] overflow-hidden">
        <img src={img.mountain} alt="Sierra Calderona" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Label className="text-white/70">Sierra Calderona · Valencia · Spain</Label>
        </div>
      </section>

      {/* 004 — NON-NEGOTIABLES */}
      <section className="bg-[#F8F6F1] py-20 sm:py-32 px-6 sm:px-10">
        <div className="max-w-[1600px] mx-auto">
          <Num n={4} />
          <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-5 mb-16 leading-[1.1] tracking-[0.02em] font-light">
            THE NON-NEGOTIABLES.
          </h2>
          <div className="grid md:grid-cols-3 gap-[1px] bg-[#1A1A1A]/[0.06]">
            {[
              { id: "01", title: "NO ALCOHOL", text: "Not reduced. Not optional. Zero. The energy of the house depends on this. It's the single design choice that changes everything." },
              { id: "02", title: "APPLICATION ONLY", text: "We review every request. Not everyone's the right fit. The community works because we're intentional about who's in it." },
              { id: "03", title: "MINIMUM ONE MONTH", text: "Real community takes time. You can't build it in a week. Most people stay two or three months. Some don't want to leave." }
            ].map((item) => (
              <div key={item.id} className="bg-[#F8F6F1] p-8 sm:p-12">
                <span className="text-[#C85A38] text-[10px] tracking-[0.4em] font-bold" style={sm}>{item.id}</span>
                <h3 className="text-[#1A1A1A] text-lg sm:text-xl mt-4 mb-4 tracking-[0.08em] font-medium">{item.title}</h3>
                <p className="text-[#1A1A1A]/40 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 005 — ROOMS */}
      <section className="bg-[#F4F1EC] py-20 sm:py-32 px-6 sm:px-10">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-start justify-between mb-16">
            <div><Num n={5} /><h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-5 leading-[1.1] tracking-[0.02em] font-light">ROOMS.</h2></div>
            <Link to="/rooms" className="mt-8"><Label className="text-[#1A1A1A]/30 hover:text-[#1A1A1A] transition-colors border-b border-[#1A1A1A]/10 pb-1 hover:border-[#1A1A1A]/30">View all <ArrowRight className="inline w-3 h-3 ml-1" /></Label></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-[1px] bg-[#1A1A1A]/[0.06]">
            {[
              { name: "GARDEN ROOM", price: "€1,200", note: "Ground floor · Pool" },
              { name: "SKY ROOM", price: "€1,300", note: "Best light · Waitlist" },
              { name: "TOWER ROOM", price: "€1,450", note: "Top floor · Private" },
              { name: "JUNIOR SUITE", price: "€1,450", note: "South terrace" },
              { name: "LUXURY SUITE", price: "€1,700", note: "Panoramic views" },
              { name: "APARTMENT", price: "€2,100", note: "Private kitchen" }
            ].map((room, i) => (
              <div key={i} className="bg-[#F4F1EC] p-6 sm:p-8 hover:bg-[#EFECE6] transition-colors group cursor-pointer">
                <p className="text-[#1A1A1A]/15 text-[9px] tracking-[0.3em] mb-8" style={sm}>{room.note}</p>
                <p className="text-[#1A1A1A]/70 text-xs sm:text-sm tracking-[0.12em] font-medium mb-2 group-hover:text-[#1A1A1A] transition-colors">{room.name}</p>
                <p className="text-[#C85A38] text-xl sm:text-2xl font-light">{room.price}<span className="text-[#1A1A1A]/10 text-xs tracking-wider">/MO</span></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 006 — A DAY */}
      <section className="bg-[#F8F6F1] py-20 sm:py-32 px-6 sm:px-10">
        <div className="max-w-[1600px] mx-auto">
          <Num n={6} />
          <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-5 mb-16 leading-[1.1] tracking-[0.02em] font-light">A DAY AT SAVI.</h2>
          <div className="border-l border-[#1A1A1A]/10 ml-2 sm:ml-4">
            {[
              { time: "07:00", text: "Coffee on the terrace. Sunrise over the mountains. The dogs are already doing laps." },
              { time: "09:00", text: "Deep work. Coworking space fills up. Screens on, headphones in, shipping." },
              { time: "13:00", text: "Break. Gym, pool, a walk in the sierra. Some drive into Valencia." },
              { time: "15:00", text: "Afternoon block. Skill shares. Accountability sessions." },
              { time: "20:00", text: "Someone cooks. Roof terrace. Conversations that go somewhere." },
              { time: "23:00", text: "Quiet. Tomorrow you wake up clear. Again." }
            ].map((item, i) => (
              <div key={i} className="pl-8 sm:pl-12 pb-10 relative">
                <div className="absolute left-0 top-1.5 w-[5px] h-[5px] rounded-full bg-[#C85A38] -translate-x-[3px]" />
                <p className="text-[#C85A38] text-[11px] tracking-[0.4em] mb-2" style={sm}>{item.time}</p>
                <p className="text-[#1A1A1A]/50 text-sm leading-relaxed max-w-lg">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FULL BLEED ROOM */}
      <section className="relative h-[50vh] overflow-hidden">
        <img src={img.room} alt="Room at Savi" className="absolute inset-0 w-full h-full object-cover" />
      </section>

      {/* 007 — STEVE */}
      <section className="bg-[#F8F6F1] py-20 sm:py-32 px-6 sm:px-10">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-[180px_1fr] gap-12 sm:gap-16 items-start">
          <div>
            <Num n={7} />
            <img src={STEVE_IMG} alt="Steve Haynes" className="w-28 h-28 sm:w-36 sm:h-36 rounded-full object-cover mt-6 grayscale hover:grayscale-0 transition-all duration-700" />
          </div>
          <div className="max-w-xl">
            <Label className="text-[#1A1A1A]/20 mb-6 block">From the founder</Label>
            <blockquote className="text-xl sm:text-2xl md:text-3xl text-[#1A1A1A]/80 leading-snug mb-6 font-light">
              "I built this because I was tired of surface-level everything."
            </blockquote>
            <p className="text-[#1A1A1A]/40 text-sm leading-relaxed mb-4">
              Coworkings where nobody talks. Wellness retreats that feel like spas. Coliving spaces that are basically hostels with better marketing.
            </p>
            <p className="text-[#1A1A1A]/40 text-sm leading-relaxed mb-8">
              This place is simple. A villa in the mountains where interesting people come to work and live together for a while. No alcohol, no drama, no bullshit.
            </p>
            <p className="text-[#1A1A1A]/70 text-sm font-medium">Steve Haynes</p>
          </div>
        </div>
      </section>

      {/* 008 — QUIZ */}
      <section id="v2-quiz" className="bg-[#F4F1EC] py-20 sm:py-32 px-6 sm:px-10">
        <div className="max-w-[1600px] mx-auto">
          <Num n={8} />
          <div className="text-center mt-5 mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mb-4 tracking-[0.02em] font-light">IS THIS FOR YOU?</h2>
            <p className="text-[#1A1A1A]/30 text-sm">Four questions. Thirty seconds.</p>
          </div>
          <SaviQuiz />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#F8F6F1] border-t border-[#1A1A1A]/5 py-10 px-6 sm:px-10">
        <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <span className="text-[#1A1A1A]/20 text-xs tracking-[0.5em]" style={{fontFamily: 'Cinzel, serif'}}>SAVI</span>
          <div className="flex flex-wrap justify-center gap-6">
            {[
              { to: "/rooms", label: "Rooms" }, { to: "/about", label: "About" }, { to: "/studio", label: "Studio" },
              { to: "/sober-coliving", label: "Journal" }, { to: "/coliving-valencia", label: "Coliving Valencia" },
              { to: "/contact", label: "Apply" }, { to: "/privacy", label: "Privacy" }
            ].map((l) => (
              <Link key={l.to} to={l.to}><Label className="text-[#1A1A1A]/15 hover:text-[#1A1A1A]/40 transition-colors">{l.label}</Label></Link>
            ))}
          </div>
          <Label className="text-[#1A1A1A]/10">&copy; 2026</Label>
        </div>
      </footer>
    </div>
  );
};

export default HomeV2Page;
